import { blogPosts } from '../data/blog-posts'

/**
 * Route equivalence across locales — the single source for hreflang alternates
 * (HreflangManager), the canonical (CanonicalManager) and the language
 * switcher (Navbar). Keep in sync with src/Router.jsx.
 */

/** The production origin: canonical and hreflang URLs are built on it. */
export const SITE_URL = 'https://ankaplastik.com'

/** @typedef {{ tr: string, en: string, de: string }} RouteGroup */

/** @type {ReadonlyArray<RouteGroup>} */
export const ROUTE_GROUPS = Object.freeze([
  { tr: '/', en: '/en', de: '/de' },
  { tr: '/tampon-baski', en: '/en/pad-printing', de: '/de/tampondruck' },
  { tr: '/serigrafi', en: '/en/screen-printing', de: '/de/siebdruck' },
  { tr: '/montaj-trim', en: '/en/assembly-trim', de: '/de/montage-trim' },
  { tr: '/kurumsal', en: '/en/corporate', de: '/de/unternehmen' },
  { tr: '/blog', en: '/en/blog', de: '/de/blog' },
])

export const HOME_BY_LANG = Object.freeze({ tr: '/', en: '/en', de: '/de' })

/** Routes that carry their own #contact RFQ section. */
const RFQ_GROUP_INDEXES = [0, 1, 2, 3]

/**
 * @param {string} pathname
 * @returns {string} pathname without a trailing slash ('/' stays '/')
 */
export function normalizePath(pathname) {
  return pathname.replace(/\/+$/, '') || '/'
}

/**
 * Locale of a path: /en… → 'en', /de… → 'de', everything else → 'tr'.
 *
 * @param {string} pathname
 * @returns {'tr' | 'en' | 'de'}
 */
export function langFromPath(pathname) {
  if (pathname === '/en' || pathname.startsWith('/en/')) return 'en'
  if (pathname === '/de' || pathname.startsWith('/de/')) return 'de'
  return 'tr'
}

/** Slugs of the published posts: an unknown slug is no page (BlogPost shows 404). */
const BLOG_SLUGS = new Set(blogPosts.map((post) => post.slug))

/**
 * Blog posts share one slug across locales: /blog/:slug, /en/blog/:slug, /de/blog/:slug
 *
 * @param {string} path normalized path
 * @returns {RouteGroup | null}
 */
function blogPostGroup(path) {
  const match = path.match(/^\/(?:(?:en|de)\/)?blog\/([^/]+)$/)
  if (!match || !BLOG_SLUGS.has(match[1])) return null
  const slug = match[1]
  return { tr: `/blog/${slug}`, en: `/en/blog/${slug}`, de: `/de/blog/${slug}` }
}

/**
 * The locale equivalents of a path, or null for an unknown route.
 *
 * @param {string} pathname
 * @returns {RouteGroup | null}
 */
export function alternatesFor(pathname) {
  const path = normalizePath(pathname)
  return (
    blogPostGroup(path) ||
    ROUTE_GROUPS.find((g) => g.tr === path || g.en === path || g.de === path) ||
    null
  )
}

/**
 * True when the current page renders its own #contact RFQ section.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function hasOwnRfq(pathname) {
  const path = normalizePath(pathname)
  return RFQ_GROUP_INDEXES.some((i) => {
    const g = ROUTE_GROUPS[i]
    return g.tr === path || g.en === path || g.de === path
  })
}
